import React, { useState } from 'react';
import moment from 'moment-jalaali';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LockClockIcon from '@mui/icons-material/LockClock';
import StarsIcon from '@mui/icons-material/Stars';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircleIcon from '@mui/icons-material/Circle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EditIcon from '@mui/icons-material/Edit';
import { toPersianDigits } from './JalaliDatePicker';

/**
 * رندر عمومی تایم‌لاین عمودی رویدادها (جلالی، RTL) — برای سفارش‌ها
 * مشترک است. دیتا از بیرون داده می‌شود؛ fetch با کامپوننت والد است.
 */

export interface TimelineEvent {
  at: string;
  category: string;
  type: string;
  title: string;
  description?: string;
  actorRole?: string;
  actorName?: string;
  itemTitle?: string;
  isFuture?: boolean;
  meta?: Record<string, any>;
}

interface CategoryMeta {
  label: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  // سفارش‌ها
  order: { label: 'سفارش', color: '#1565c0', bg: '#e3f2fd', icon: <ReceiptIcon fontSize="small" /> },
  payment: { label: 'پرداخت', color: '#2e7d32', bg: '#e8f5e9', icon: <PaymentIcon fontSize="small" /> },
  delivery: { label: 'تحویل', color: '#00897b', bg: '#e0f2f1', icon: <Inventory2Icon fontSize="small" /> },
  qr: { label: 'تحویل QR', color: '#6a1b9a', bg: '#f3e5f5', icon: <QrCode2Icon fontSize="small" /> },
  postal: { label: 'پستی', color: '#ef6c00', bg: '#fff3e0', icon: <LocalShippingIcon fontSize="small" /> },
  inspection: { label: 'بازرسی', color: '#00838f', bg: '#e0f7fa', icon: <SearchIcon fontSize="small" /> },
  dispute: { label: 'اختلاف', color: '#c62828', bg: '#ffebee', icon: <GavelIcon fontSize="small" /> },
  wallet: { label: 'کیف پول', color: '#283593', bg: '#e8eaf6', icon: <AccountBalanceWalletIcon fontSize="small" /> },
  escrow: { label: 'اسکرو', color: '#f9a825', bg: '#fffde7', icon: <LockClockIcon fontSize="small" /> },
  points: { label: 'امتیاز', color: '#558b2f', bg: '#f1f8e9', icon: <StarsIcon fontSize="small" /> },
  // نظرات محصول
  review: { label: 'نظر', color: '#1565c0', bg: '#e3f2fd', icon: <RateReviewIcon fontSize="small" /> },
  reply: { label: 'پاسخ', color: '#00897b', bg: '#e0f2f1', icon: <ChatBubbleOutlineIcon fontSize="small" /> },
  edit: { label: 'ویرایش', color: '#ef6c00', bg: '#fff3e0', icon: <EditIcon fontSize="small" /> },
  moderation: { label: 'مدیریت', color: '#6a1b9a', bg: '#f3e5f5', icon: <VerifiedUserIcon fontSize="small" /> },
};

const DEFAULT_CATEGORY: CategoryMeta = {
  label: 'رویداد',
  color: '#546e7a',
  bg: '#eceff1',
  icon: <CircleIcon fontSize="small" />,
};

const ACTOR_LABELS: Record<string, string> = {
  buyer: 'خریدار',
  seller: 'فروشنده',
  admin: 'ادمین',
  system: 'سیستم',
  owner: 'مالک آگهی',
  offerer: 'پیشنهاددهنده',
  user: 'کاربر',
};

const DELIVERY_METHOD_LABELS: Record<string, string> = {
  credentials: 'اعتبارنامه',
  qr: 'تحویل با QRCODE',
};

const formatDateTime = (iso: string): string => {
  if (!iso) return '-';
  try {
    return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD — HH:mm:ss'));
  } catch {
    return iso;
  }
};

const formatPrice = (v: number): string => new Intl.NumberFormat('fa-IR').format(v || 0);

const ltr = (v: any): React.ReactNode => (
  <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{String(v)}</span>
);

/** کلیدهای meta که نمایش عمومی دارند (بقیه شناسه‌های داخلی‌اند) */
const renderMeta = (meta?: Record<string, any>): React.ReactNode => {
  if (!meta) return null;
  const rows: Array<[string, React.ReactNode]> = [];

  if (typeof meta.amount === 'number') rows.push(['مبلغ', `${formatPrice(meta.amount)} تومان`]);
  if (typeof meta.refundAmount === 'number') rows.push(['مبلغ بازپرداخت', `${formatPrice(meta.refundAmount)} تومان`]);
  if (typeof meta.sellerNetAmount === 'number') rows.push(['سهم فروشنده', `${formatPrice(meta.sellerNetAmount)} تومان`]);
  if (typeof meta.penaltyAccrued === 'number') rows.push(['جریمه انباشته', `${formatPrice(meta.penaltyAccrued)} تومان`]);
  if (typeof meta.totalAmount === 'number') rows.push(['مبلغ سفارش', `${formatPrice(meta.totalAmount)} تومان`]);
  if (meta.provider) rows.push(['درگاه', String(meta.provider)]);
  if (meta.refId) rows.push(['کد مرجع', ltr(meta.refId)]);
  if (meta.trackingCode) rows.push(['کد رهگیری', ltr(meta.trackingCode)]);
  if (meta.transactionId) rows.push(['شناسه تراکنش', ltr(meta.transactionId)]);
  if (meta.username) rows.push(['نام کاربری', ltr(meta.username)]);
  if (meta.deliveredByType) rows.push(['تحویل توسط', meta.deliveredByType === 'employee' ? 'کارمند فروشگاه' : 'مالک فروشگاه']);
  if (meta.inspectorStoreName) rows.push(['بازرس', String(meta.inspectorStoreName)]);
  if (meta.inspectionCode) rows.push(['کد بازرسی', ltr(meta.inspectionCode)]);
  if (meta.resolutionType) rows.push(['نوع رأی', String(meta.resolutionType)]);
  if (typeof meta.disputedQuantity === 'number') rows.push(['تعداد مورد اختلاف', toPersianDigits(String(meta.disputedQuantity))]);
  if (typeof meta.pausedMs === 'number' && meta.pausedMs > 0) {
    rows.push(['مدت توقف', `${toPersianDigits(String(Math.round(meta.pausedMs / 3600000)))} ساعت`]);
  }
  if (typeof meta.itemsCount === 'number') rows.push(['تعداد آیتم', toPersianDigits(String(meta.itemsCount))]);
  if (meta.deliveryMethod) rows.push(['روش تحویل', DELIVERY_METHOD_LABELS[meta.deliveryMethod] || String(meta.deliveryMethod)]);
  if (typeof meta.round === 'number' && meta.round > 1) rows.push(['راند', toPersianDigits(String(meta.round))]);
  if (meta.offerStatus) rows.push(['وضعیت پیشنهاد', String(meta.offerStatus)]);
  if (meta.reason) rows.push(['دلیل', String(meta.reason)]);
  if (typeof meta.points === 'number') rows.push(['امتیاز جریمه', toPersianDigits(String(meta.points))]);
  if (meta.userName) rows.push(['کاربر', String(meta.userName)]);
  if (meta.ip) rows.push(['IP', ltr(meta.ip)]);
  // نظرات محصول
  if (typeof meta.rating === 'number') rows.push(['امتیاز', `${toPersianDigits(String(meta.rating))} از ۵`]);
  if (typeof meta.helpful === 'number') rows.push(['👍 مفید', toPersianDigits(String(meta.helpful))]);
  if (typeof meta.unhelpful === 'number') rows.push(['👎 غیرمفید', toPersianDigits(String(meta.unhelpful))]);
  if (meta.verifiedPurchase === true) rows.push(['خرید', 'تاییدشده']);

  // دیتای واردشدهٔ خریدار در چک‌اوت (Player ID و…)
  if (meta.data && typeof meta.data === 'object') {
    for (const [k, v] of Object.entries(meta.data as Record<string, unknown>)) {
      rows.push([k, ltr(v)]);
    }
  }

  if (rows.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 6 }}>
      {rows.map(([k, v], i) => (
        <span key={i} style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
          {k}: <strong style={{ color: 'var(--color-text-primary, #333)' }}>{v}</strong>
        </span>
      ))}
    </div>
  );
};

interface EventTimelineProps {
  events: TimelineEvent[];
  onRefresh?: () => void;
}

const EventTimeline: React.FC<EventTimelineProps> = ({ events, onRefresh }) => {
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  if (!events || events.length === 0) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 13 }}>
        رویدادی ثبت نشده است
      </div>
    );
  }

  const catOf = (c: string) => CATEGORY_META[c] || DEFAULT_CATEGORY;
  const presentCategories = Array.from(new Set(events.map((e) => e.category)));
  const filtered = categoryFilter ? events.filter((e) => e.category === categoryFilter) : events;

  return (
    <div>
      {/* فیلتر دسته‌بندی + رفرش */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <button
          onClick={() => setCategoryFilter('')}
          style={{
            padding: '3px 10px',
            fontSize: 11,
            borderRadius: 12,
            cursor: 'pointer',
            border: '1px solid var(--color-border, #ddd)',
            background: categoryFilter === '' ? 'var(--color-primary, #8E3D59)' : 'transparent',
            color: categoryFilter === '' ? '#fff' : 'var(--color-text-secondary)',
          }}
        >
          همه ({toPersianDigits(String(events.length))})
        </button>
        {presentCategories.map((cat) => {
          const m = catOf(cat);
          const count = events.filter((e) => e.category === cat).length;
          const active = categoryFilter === cat;
          return (
            <button
              key={cat}
              onClick={() => setCategoryFilter(active ? '' : cat)}
              style={{
                padding: '3px 10px',
                fontSize: 11,
                borderRadius: 12,
                cursor: 'pointer',
                border: `1px solid ${m.color}`,
                background: active ? m.color : m.bg,
                color: active ? '#fff' : m.color,
              }}
            >
              {m.label} ({toPersianDigits(String(count))})
            </button>
          );
        })}
        {onRefresh && (
          <button
            onClick={onRefresh}
            title="بروزرسانی تایم‌لاین"
            style={{ marginRight: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
          >
            <RefreshIcon fontSize="small" />
          </button>
        )}
      </div>

      {/* خط تایم‌لاین */}
      <div style={{ position: 'relative', paddingRight: 20 }}>
        <div
          style={{
            position: 'absolute',
            right: 15,
            top: 8,
            bottom: 8,
            width: 2,
            background: 'var(--color-border, #e0e0e0)',
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filtered.map((ev, idx) => {
            const m = catOf(ev.category);
            return (
              <div key={idx} style={{ position: 'relative', opacity: ev.isFuture ? 0.65 : 1 }}>
                {/* نقطه روی خط */}
                <div
                  style={{
                    position: 'absolute',
                    right: -13,
                    top: 6,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: m.bg,
                    color: m.color,
                    border: `2px solid ${m.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(ev.isFuture ? { borderStyle: 'dashed' } : {}),
                  }}
                >
                  {m.icon}
                </div>
                {/* کارت رویداد */}
                <div
                  style={{
                    marginRight: 24,
                    padding: '8px 12px',
                    background: 'var(--color-bg-default, #fafafa)',
                    border: '1px solid var(--color-border, #e0e0e0)',
                    borderRadius: 8,
                    ...(ev.isFuture ? { borderStyle: 'dashed' } : {}),
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{ev.title}</span>
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 8px',
                        borderRadius: 10,
                        background: m.bg,
                        color: m.color,
                        border: `1px solid ${m.color}`,
                      }}
                    >
                      {m.label}
                    </span>
                    {ev.actorRole && (
                      <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, background: '#eceff1', color: '#455a64' }}>
                        {ACTOR_LABELS[ev.actorRole] || ev.actorRole}
                        {ev.actorName ? `: ${ev.actorName}` : ''}
                      </span>
                    )}
                    {ev.isFuture && (
                      <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, background: '#fff8e1', color: '#f57f17', border: '1px solid #f9a825' }}>
                        رویداد آینده
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    <span>{formatDateTime(ev.at)}</span>
                    {ev.itemTitle && <span>آیتم: {ev.itemTitle}</span>}
                  </div>
                  {ev.description && (
                    <div style={{ fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap' }}>{ev.description}</div>
                  )}
                  {renderMeta(ev.meta)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EventTimeline;
